import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, UploadSimple } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const QUESTIONS = [
  "Tell me about the last person you cared for — what did a good day with them look like?",
  "Think of a client who was having a hard day — refusing care, upset, or scared. What did you actually do?",
  "Everyone gets frustrated at work sometimes. What does that look like for you, and what do you do with it?",
  "You arrive for a visit and something feels wrong with a client. Walk me through what you do, step by step.",
  "Tell me about a time you made a mistake on the job. What happened next?",
  "A family member questions how you're doing things in front of the client. How do you handle it?",
  "If we called your last client's family right now, what would they say about you?",
  "What do you need from the office to do your best work?",
];
const OBSERVATIONS = [["on_time", "Candidate was on time"], ["hygiene", "Dressed appropriately, good hygiene"],
  ["demeanor", "Demeanor appropriate and engaged"], ["experience", "Has previous experience (formal or informal)"]];
const ID_TYPES = ["Driver's license", "State photo ID", "Passport", "Other"];
const DOCS = [["id_card", "Photo ID (DL/state ID)"], ["ss_card", "SS card"], ["tb_test", "TB test"],
  ["child_abuse_clearance", "Child abuse clearance"], ["cna_registration", "CNA registration"],
  ["passport", "Passport"], ["green_card", "Green card"], ["other", "Other"]];


const YesNo = ({ value, onChange, testid }) => (
  <div className="flex gap-2" data-testid={testid}>
    {["Yes", "No"].map((v) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors ${value === v ? "border-primary bg-primary text-white shadow-sm" : "border-stone-200 bg-white text-stone-500 hover:border-primary/40 hover:text-primary"}`}
        data-testid={`${testid}-${v.toLowerCase()}`}>
        {v}
      </button>
    ))}
  </div>
);
function DocUpload({ aid, kind, label, meta, onMeta }) {
  const ref = useRef(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const up = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      fd.append("doc_number", meta?.number || "");
      fd.append("issued", meta?.issued || "");
      fd.append("expires", meta?.expires || "");
      fd.append("tb_method", meta?.method || "");
      fd.append("tb_step1", meta?.step1 || "");
      fd.append("tb_step2", meta?.step2 || "");
      const { data } = await api.post(`/applicants/${aid}/documents?kind=${kind}`, fd);
      setDone(files[0].name);
      // OCR: pre-fill any details the document itself gave us (office verifies before saving the form)
      const read = (data?.saved || []).find((s) => s.read && Object.keys(s.read).length)?.read;
      if (read) {
        if (!meta?.number && (read.id_number || read.doc_number)) onMeta(kind, "number", read.id_number || read.doc_number);
        if (!meta?.issued && (read.issue_date || read.date_given)) onMeta(kind, "issued", read.issue_date || read.date_given);
        if (!meta?.expires && read.expiration) onMeta(kind, "expires", read.expiration);
        toast.success(`${label} attached — details read from the document${read.full_name ? ` (${read.full_name})` : ""}; please verify below`);
      } else {
        toast.success(`${label} attached`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50 p-3" data-testid={`iv-doc-${kind}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-stone-700">{label}</p>
        <div className="flex items-center gap-2">
          <input ref={ref} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif" className="hidden" onChange={(e) => { up(e.target.files); e.target.value = ""; }} data-testid={`iv-upload-input-${kind}`} />
          <Button size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={busy} className="rounded-full border-stone-300 text-xs" data-testid={`iv-upload-${kind}`}>
            <UploadSimple size={13} className="mr-1" /> {busy ? "Scanning & reading…" : "Scan"}
          </Button>
          {done && <span className="text-[11px] font-medium text-[#1e8449]" data-testid={`iv-uploaded-${kind}`}>✓ {done}</span>}
        </div>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2">
        <Input value={meta?.number || ""} onChange={(e) => onMeta(kind, "number", e.target.value)} placeholder="Doc number"
          className="h-8 border-stone-300 bg-white text-xs" data-testid={`iv-docnum-${kind}`} />
        <Input type="date" value={meta?.issued || ""} onChange={(e) => onMeta(kind, "issued", e.target.value)} title="Issue date"
          className="h-8 border-stone-300 bg-white text-xs" data-testid={`iv-docissued-${kind}`} />
        <Input type="date" value={meta?.expires || ""} onChange={(e) => onMeta(kind, "expires", e.target.value)} title="Expiration date"
          className="h-8 border-stone-300 bg-white text-xs" data-testid={`iv-docexpires-${kind}`} />
      </div>
      {kind === "tb_test" && (
        <div className="mt-2 space-y-2" data-testid="iv-tb-method-block">
          <select value={meta?.method || ""} onChange={(e) => onMeta(kind, "method", e.target.value)}
            className="h-8 w-full rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="iv-tb-method">
            <option value="">TB method…</option>
            <option value="two_step">Two-step skin test</option>
            <option value="one_step_recent">One-step within the last year (step 2 due within 10 days of start)</option>
            <option value="blood">TB Gold / blood test</option>
            <option value="xray">Chest x-ray</option>
          </select>
          {(meta?.method === "two_step" || meta?.method === "one_step_recent") && (
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={meta?.step1 || ""} onChange={(e) => onMeta(kind, "step1", e.target.value)} title="Step 1 date"
                className="h-8 border-stone-300 bg-white text-xs" data-testid="iv-tb-step1" />
              <Input type="date" value={meta?.step2 || ""} onChange={(e) => onMeta(kind, "step2", e.target.value)} title="Step 2 date (if already done)"
                className="h-8 border-stone-300 bg-white text-xs" data-testid="iv-tb-step2" />
            </div>
          )}
          {meta?.method === "one_step_recent" && !meta?.step2 && (
            <p className="text-[11px] font-medium text-[#8a5d14]">They can start — step 2 must be completed within 10 days of the start date, and the two tests combined must stay within one year.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function InterviewFormPage() {
  const { aid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [outcome, setOutcome] = useState("showed");
  const showed = outcome === "showed";
  const [noShow, setNoShow] = useState({ reason: "", action: "", reschedule_at: "" });
  const [uploadKind, setUploadKind] = useState("other");
  const [form, setForm] = useState({
    conducted_by: "", interview_date: "", face_to_face: false, q_ratings: Array(8).fill(""), questions_notes: "",
    observations: {}, dob: "", address: "", ssn: "",
    id_type: "Driver's license", id_state: "PA", id_number: "", id_issue: "", id_expiry: "",
    dob_verified: false, ssn_verified: false, docs_presented: [], docs_needed: [], doc_meta: {},
    lives_in_pa: "", pa_resident_2yr: "",
    drug_test: { documented: false, result: "", date: "", notes: "" },
    decision: "", provisional: false, provisional_note: "", reason: "",
    offer_position: "", offer_pay_rate: "", offer_evv_bonus: "", offer_differential: "", offer_start_date: "", offer_schedule: "",
    items: { badge: false, clear_bag: false, shirt: { qty: "", price: "" }, pants: { qty: "", price: "" }, hoodie: { qty: "", price: "" }, sweatshirt: { qty: "", price: "" } },
    dedicated_caregiver: "", dedicated_client: "", dedicated_attest: false,
  });
  const itemsTotal = ["shirt", "pants", "hoodie", "sweatshirt"].reduce((s, k) => {
    const it = form.items[k];
    return s + (parseFloat(it.price) || 0) * (parseInt(it.qty) || 0);
  }, 0);
  const [busy, setBusy] = useState(false);
  const [fbiBusy, setFbiBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setDocMeta = (kind, field, v) => setForm((f) => ({ ...f, doc_meta: { ...f.doc_meta, [kind]: { ...(f.doc_meta[kind] || {}), [field]: v } } }));

  const bgDetermination = () => {
    if (!form.lives_in_pa || !form.pa_resident_2yr) return null;
    const reasons = [];
    if (form.lives_in_pa !== "yes") reasons.push("doesn't currently live in PA");
    if (form.pa_resident_2yr !== "yes") reasons.push("hasn't lived in PA for the last 2 years (verbal)");
    if (form.id_state !== "PA") reasons.push("ID issued by another state");
    if (reasons.length) return { cls: "border-[#C0392B]/40 bg-[#C0392B]/5 text-[#C0392B]", text: `FBI background check required — ${reasons.join("; ")}` };
    if (form.id_issue && new Date(form.id_issue) > new Date(Date.now() - 730 * 86400000))
      return { cls: "border-[#B7791F]/40 bg-[#B7791F]/5 text-[#8a5d14]", text: "Proof of PA residency required — PA resident 2+ years (verbal), but the ID was issued less than 2 years ago" };
    return { cls: "border-[#27AE60]/40 bg-[#27AE60]/5 text-[#1e8449]", text: "No FBI check or residency proof needed — PA resident 2+ years, PA ID issued 2+ years ago" };
  };

  useEffect(() => {
    api.get("/applicants").then((r) => {
      const found = (r.data.applicants || []).find((x) => x.id === aid);
      if (!found) { toast.error("Applicant not found"); navigate("/hiring"); return; }
      setA(found);
      setForm((f) => ({ ...f, conducted_by: f.conducted_by || user?.name || "",
                         interview_date: (found.interview_at || "").replace(" ", "T").slice(0, 16) }));
    }).catch((e) => toast.error(formatApiError(e)));
  }, [aid]);

  const submit = async () => {
    if (form.decision === "proceed" && !window.confirm(
      "PROCEED TO HIRE will:\n• Send them an offer-letter signing link (pay rate above)\n• The moment they SIGN the offer: reference-check links + employer verification requests go out automatically\n• Employee account + employee ID created on signature, login invite sent, I-9 pre-filled from this form\n\nContinue?")) return;
    setBusy(true);
    try {
      const payload = showed ? { ...form, showed_up: true }
        : { showed_up: false, outcome, no_show_reason: noShow.reason, no_show_followup: noShow.action, reschedule_at: noShow.reschedule_at };
      const { data } = await api.post(`/applicants/${aid}/interview`, payload);
      if (data.status === "hired") {
        toast.success("Hired! Account created — references go out when they sign the offer; invite + pre-filled I-9 sent.");
      } else {
        toast.success(`Saved — ${data.status.replace("_", " ")}`);
      }
      navigate("/hiring");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!a) return <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="interview-form-page">
      <button onClick={() => navigate("/hiring")} className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid="interview-back">
        <ArrowLeft size={14} /> Back to Hiring
      </button>
      <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
        <h1 className="font-heading text-2xl font-bold text-stone-900" data-testid="interview-title">Face-to-Face Interview — Notes & Verification — {a.name}</h1>
        <p className="mt-1 text-sm text-stone-500">{a.email}{a.phone ? ` · ${a.phone}` : ""} · {a.position}</p>
        <p className="mt-1 text-xs text-stone-400">This record serves as the face-to-face interview verification (28 Pa. Code § 611.55).</p>
        {(a.references || []).length > 0 && (
          <p className="mt-1 text-xs text-stone-400">References on file: {a.references.map((r) => `${r.name} (${r.relationship || "?"})`).join(", ")}</p>
        )}
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="interview-showed">
        <p className="text-sm font-semibold text-stone-800">Did the candidate show up?</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {[["showed", "Yes — proceed"], ["no_show", "No-show"], ["rescheduled", "Rescheduled"], ["cancelled", "Cancelled"]].map(([v, label]) => (
            <button key={v} onClick={() => setOutcome(v)}
              className={`rounded-full border px-4 py-2 text-sm font-semibold ${outcome === v ? "border-primary/50 bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-400"}`}
              data-testid={`interview-outcome-${v}`}>{label}</button>
          ))}
        </div>
        {outcome !== "showed" && (
          <div className="mt-4 space-y-3" data-testid="interview-noshow">
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">{outcome === "cancelled" ? "Cancellation reason (required)" : "Reason / what happened"}</label>
              <Input value={noShow.reason} onChange={(e) => setNoShow((n) => ({ ...n, reason: e.target.value }))} className="border-stone-300 bg-white" data-testid="noshow-reason" />
            </div>
            {outcome === "no_show" && (
              <div className="flex gap-2">
                {[["reschedule", "Reschedule them"], ["decline", "Decline"]].map(([v, label]) => (
                  <button key={v} onClick={() => setNoShow((n) => ({ ...n, action: v }))}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${noShow.action === v ? "border-primary/50 bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-400"}`}
                    data-testid={`noshow-${v}`}>{label}</button>
                ))}
              </div>
            )}
            {(outcome === "rescheduled" || (outcome === "no_show" && noShow.action === "reschedule")) && (
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">New interview date & time</label>
                <Input type="datetime-local" value={noShow.reschedule_at} onChange={(e) => setNoShow((n) => ({ ...n, reschedule_at: e.target.value }))} className="h-9 w-56 border-stone-300 bg-white text-sm" data-testid="noshow-reschedule" />
              </div>
            )}
            <Button onClick={submit}
              disabled={busy || (outcome === "cancelled" && !noShow.reason.trim()) || (outcome === "no_show" && !noShow.action)
                || (outcome === "rescheduled" && !noShow.reschedule_at) || (outcome === "no_show" && noShow.action === "reschedule" && !noShow.reschedule_at)}
              className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="noshow-save">
              {busy ? "Saving…" : `Save — ${outcome.replace("_", "-")}`}
            </Button>
          </div>
        )}
      </div>

      {showed && (
        <>
          <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="interview-meta">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">Interview conducted by</label>
                <Input value={form.conducted_by} onChange={set("conducted_by")} className="border-stone-300 bg-white" data-testid="interview-conducted-by" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">Face-to-face interview date</label>
                <Input type="datetime-local" value={form.interview_date} onChange={set("interview_date")} className="border-stone-300 bg-white" data-testid="interview-date" />
              </div>
              <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-stone-200 bg-accent/40 p-3 text-sm text-stone-700 sm:col-span-2" data-testid="interview-f2f-label">
                <input type="checkbox" className="mt-0.5" checked={form.face_to_face} onChange={(e) => setForm((f) => ({ ...f, face_to_face: e.target.checked }))} data-testid="interview-face-to-face" />
                <span><b>I verify this interview was conducted face-to-face</b> with the candidate, and this record serves as the face-to-face interview verification.</span>
              </label>
            </div>
          </div>

          <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="interview-questions">
            <p className="font-heading text-sm font-semibold text-stone-900">Application review questions</p>
            <div className="mt-3 space-y-3">
              {QUESTIONS.map((q, i) => (
                <div key={i} className="rounded-lg border border-stone-100 bg-stone-50 p-3">
                  <p className="text-sm text-stone-700">{q}</p>
                  <div className="mt-2 flex gap-2">
                    {[["satisfactory", "Satisfactory"], ["unsatisfactory", "Unsatisfactory"]].map(([v, label]) => (
                      <button key={v} onClick={() => setForm((f) => ({ ...f, q_ratings: f.q_ratings.map((r, j) => (j === i ? v : r)) }))}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold ${form.q_ratings[i] === v ? (v === "satisfactory" ? "border-[#27AE60]/50 bg-[#27AE60]/10 text-[#1e8449]" : "border-[#C0392B]/50 bg-[#C0392B]/10 text-[#C0392B]") : "border-stone-200 bg-white text-stone-400"}`}
                        data-testid={`interview-q${i + 1}-${v}`}>{label}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <label className="mb-1 block text-xs font-medium text-stone-500">Question notes (shared)</label>
              <Textarea value={form.questions_notes} onChange={set("questions_notes")} className="min-h-16 border-stone-300 bg-white text-sm" data-testid="interview-questions-notes" />
            </div>
          </div>

          <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="interview-observations">
            <p className="font-heading text-sm font-semibold text-stone-900">Observations</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {OBSERVATIONS.map(([k, label]) => (
                <label key={k} className="flex cursor-pointer items-center gap-2 text-sm text-stone-700" data-testid={`interview-obs-${k}-label`}>
                  <input type="checkbox" checked={!!form.observations[k]}
                    onChange={(e) => setForm((f) => ({ ...f, observations: { ...f.observations, [k]: e.target.checked } }))}
                    data-testid={`interview-obs-${k}`} />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="interview-identity">
            <p className="font-heading text-sm font-semibold text-stone-900">Identity & documents</p>
            <p className="mt-0.5 text-xs text-stone-400">Recorded here, this pre-fills the employee's I-9 — they sign it in their portal, you countersign on their profile.</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">Date of birth (verified against ID)</label>
                <Input type="date" value={form.dob} onChange={set("dob")} className="border-stone-300 bg-white" data-testid="interview-dob" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">Address</label>
                <Input value={form.address} onChange={set("address")} className="border-stone-300 bg-white" data-testid="interview-address" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">SSN (from SS card)</label>
                <Input value={form.ssn} onChange={set("ssn")} placeholder="9 digits" className="border-stone-300 bg-white" data-testid="interview-ssn" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">ID type</label>
                <select value={form.id_type} onChange={set("id_type")} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="interview-id-type">
                  {ID_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">ID state</label>
                <select value={form.id_state} onChange={set("id_state")} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="interview-id-state">
                  <option value="PA">PA</option><option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">ID number</label>
                <Input value={form.id_number} onChange={set("id_number")} className="border-stone-300 bg-white" data-testid="interview-id-number" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">ID issue date</label>
                <Input type="date" value={form.id_issue} onChange={set("id_issue")} className="border-stone-300 bg-white" data-testid="interview-id-issue" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">ID expiration date</label>
                <Input type="date" value={form.id_expiry} onChange={set("id_expiry")} className="border-stone-300 bg-white" data-testid="interview-id-expiry" />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-700" data-testid="interview-dob-verified-label">
                <input type="checkbox" checked={form.dob_verified} onChange={(e) => setForm((f) => ({ ...f, dob_verified: e.target.checked }))} data-testid="interview-dob-verified" />
                DOB visually verified
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-700" data-testid="interview-ssn-verified-label">
                <input type="checkbox" checked={form.ssn_verified} onChange={(e) => setForm((f) => ({ ...f, ssn_verified: e.target.checked }))} data-testid="interview-ssn-verified" />
                SSN card visually verified
              </label>
            </div>
            <div className="mt-4">
              <p className="mb-2 text-xs font-medium text-stone-500">Documentation presented — check what they brought, then scan it</p>
              <div className="flex flex-wrap gap-2">
                {DOCS.map(([k, label]) => (
                  <button key={k} onClick={() => setForm((f) => ({ ...f, docs_presented: f.docs_presented.includes(k) ? f.docs_presented.filter((x) => x !== k) : [...f.docs_presented, k],
                                                                     docs_needed: f.docs_presented.includes(k) ? f.docs_needed : f.docs_needed.filter((x) => x !== k) }))}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium ${form.docs_presented.includes(k) ? "border-primary/50 bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-500"}`}
                    data-testid={`interview-doc-${k}`}>{label}</button>
                ))}
              </div>
              <p className="mb-2 mt-4 text-xs font-medium text-stone-500">Still needed — they didn't bring it; becomes an outstanding onboarding item</p>
              <div className="flex flex-wrap gap-2">
                {DOCS.map(([k, label]) => (
                  <button key={k} onClick={() => setForm((f) => ({ ...f, docs_needed: f.docs_needed.includes(k) ? f.docs_needed.filter((x) => x !== k) : [...f.docs_needed, k],
                                                                     docs_presented: f.docs_needed.includes(k) ? f.docs_presented : f.docs_presented.filter((x) => x !== k) }))}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium ${form.docs_needed.includes(k) ? "border-[#B7791F]/50 bg-[#B7791F]/10 text-[#8a5d14]" : "border-stone-200 bg-white text-stone-500"}`}
                    data-testid={`interview-doc-needed-${k}`}>{label}</button>
                ))}
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {DOCS.filter(([k]) => form.docs_presented.includes(k)).map(([k, label]) => (
                  <DocUpload key={k} aid={aid} kind={k} label={label} meta={form.doc_meta[k]} onMeta={setDocMeta} />
                ))}
              </div>
              <div className="mt-3 rounded-lg border border-dashed border-stone-300 p-3">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold text-stone-700">Scan any other document</p>
                  <select value={uploadKind} onChange={(e) => setUploadKind(e.target.value)}
                    className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="iv-upload-kind">
                    {DOCS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                  </select>
                </div>
                <div className="mt-2">
                  <DocUpload aid={aid} kind={uploadKind} label={DOCS.find(([k]) => k === uploadKind)?.[1] || "Document"} meta={form.doc_meta[uploadKind]} onMeta={setDocMeta} />
                </div>
              </div>
              <p className="mt-2 text-[11px] text-stone-400">Document numbers + issue/expiration dates pre-fill the I-9. Scans attach to the employee record automatically on hire; anything not captured here becomes an outstanding onboarding item.</p>
            </div>
          </div>

          <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="interview-residency">
            <p className="font-heading text-sm font-semibold text-stone-900">Residency & background check determination</p>
            <p className="mt-0.5 text-xs text-stone-400">Asked verbally at this stage — cross-checked against the background check authorization they sign in onboarding.</p>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-1 text-xs font-medium text-stone-500">Do they currently live in PA?</p>
                <div className="flex gap-2">
                  {[["yes", "Yes"], ["no", "No"]].map(([v, label]) => (
                    <button key={v} onClick={() => setForm((f) => ({ ...f, lives_in_pa: v }))}
                      className={`rounded-full border px-4 py-1.5 text-xs font-semibold ${form.lives_in_pa === v ? "border-primary/50 bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-400"}`}
                      data-testid={`interview-lives-pa-${v}`}>{label}</button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium text-stone-500">Have they lived in PA for the last 2 years consecutively?</p>
                <div className="flex gap-2">
                  {[["yes", "Yes"], ["no", "No"]].map(([v, label]) => (
                    <button key={v} onClick={() => setForm((f) => ({ ...f, pa_resident_2yr: v }))}
                      className={`rounded-full border px-4 py-1.5 text-xs font-semibold ${form.pa_resident_2yr === v ? "border-primary/50 bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-400"}`}
                      data-testid={`interview-pa-2yr-${v}`}>{label}</button>
                  ))}
                </div>
              </div>
            </div>
            {bgDetermination() && (
              <div className={`mt-3 rounded-lg border p-3 text-xs font-semibold ${bgDetermination().cls}`} data-testid="interview-bg-determination">
                {bgDetermination().text}
              </div>
            )}
            {bgDetermination()?.requirement === "fbi" && (
              <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="interview-fbi-actions">
                <Button size="sm" variant="outline" className="border-stone-300 text-xs" disabled={fbiBusy}
                  onClick={async () => {
                    setFbiBusy(true);
                    try {
                      const { data } = await api.post(`/applicants/${aid}/fbi-instructions`);
                      toast.success(data.email === "sent"
                        ? "FBI instructions + consent form emailed to the applicant"
                        : `FBI packet prepared — email status: ${data.email} (emails are off globally; flip them on in Settings when ready)`);
                    } catch (e) { toast.error(formatApiError(e)); }
                    setFbiBusy(false);
                  }}
                  data-testid="interview-fbi-email">
                  {fbiBusy ? "Preparing…" : "Email FBI instructions + $26.50 consent form"}
                </Button>
                <span className="text-[10px] text-stone-400">IdentoGO Fairless Hills · service code from Settings · receipt required before first shift</span>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="interview-drug-test">
            <p className="font-heading text-sm font-semibold text-stone-900">Drug test</p>
            <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-stone-700" data-testid="interview-drug-done-label">
              <input type="checkbox" checked={form.drug_test.documented}
                onChange={(e) => setForm((f) => ({ ...f, drug_test: { ...f.drug_test, documented: e.target.checked, ...(e.target.checked ? {} : { result: "", date: "", notes: "" }) } }))}
                data-testid="interview-drug-done" />
              Drug test administered & documented at this interview
            </label>
            {form.drug_test.documented && (
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Result</label>
                  <select value={form.drug_test.result} onChange={(e) => setForm((f) => ({ ...f, drug_test: { ...f.drug_test, result: e.target.value } }))}
                    className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="interview-drug-result">
                    <option value="">Select…</option>
                    <option value="negative">Negative</option>
                    <option value="positive">Positive</option>
                    <option value="pending">Pending lab confirmation</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Date</label>
                  <Input type="date" value={form.drug_test.date} onChange={(e) => setForm((f) => ({ ...f, drug_test: { ...f.drug_test, date: e.target.value } }))} className="border-stone-300 bg-white" data-testid="interview-drug-date" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">Notes</label>
                  <Input value={form.drug_test.notes} onChange={(e) => setForm((f) => ({ ...f, drug_test: { ...f.drug_test, notes: e.target.value } }))} className="border-stone-300 bg-white" data-testid="interview-drug-notes" />
                </div>
              </div>
            )}
            {!form.drug_test.documented && <p className="mt-2 text-[11px] text-stone-400">If not done here, it becomes an outstanding onboarding item.</p>}
          </div>

          <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="interview-decision">
            <p className="font-heading text-sm font-semibold text-stone-900">Decision</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {[["proceed", "Proceed to hire"], ["defer", "Defer"], ["decline", "Decline"]].map(([v, label]) => (
                <button key={v} onClick={() => setForm((f) => ({ ...f, decision: v }))}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold ${form.decision === v
                    ? v === "proceed" ? "border-[#27AE60]/50 bg-[#27AE60]/10 text-[#1e8449]"
                      : v === "decline" ? "border-[#C0392B]/50 bg-[#C0392B]/10 text-[#C0392B]"
                      : "border-[#B7791F]/50 bg-[#B7791F]/10 text-[#8a5d14]"
                    : "border-stone-200 bg-white text-stone-400"}`}
                  data-testid={`interview-decision-${v}`}>{label}</button>
              ))}
            </div>
            {(form.decision === "defer" || form.decision === "decline") && (
              <div className="mt-3">
                <label className="mb-1 block text-xs font-medium text-stone-500">Reason (required)</label>
                <Textarea value={form.reason} onChange={set("reason")} className="min-h-16 border-stone-300 bg-white text-sm" data-testid="interview-reason" />
              </div>
            )}
            {form.decision === "proceed" && (
              <div className="mt-3 space-y-3">
                <div className="grid gap-3 sm:grid-cols-2" data-testid="interview-offer-fields">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-stone-500">Position</label>
                    <Input value={form.offer_position} onChange={set("offer_position")} placeholder="Direct Care Worker" className="border-stone-300 bg-white" data-testid="interview-offer-position" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-stone-500">Pay rate ($/hour) *</label>
                    <Input value={form.offer_pay_rate} onChange={set("offer_pay_rate")} placeholder="e.g. 17.00" className="border-stone-300 bg-white" data-testid="interview-offer-rate" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-stone-500">EVV bonus ($/hour, optional)</label>
                    <Input value={form.offer_evv_bonus} onChange={set("offer_evv_bonus")} placeholder="e.g. 1.00" className="border-stone-300 bg-white" data-testid="interview-offer-evv-bonus" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-stone-500">Shift differential ($/hour, optional)</label>
                    <Input value={form.offer_differential} onChange={set("offer_differential")} placeholder="e.g. 0.50" className="border-stone-300 bg-white" data-testid="interview-offer-differential" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-stone-500">Anticipated start date</label>
                    <Input type="date" value={form.offer_start_date} onChange={set("offer_start_date")} className="border-stone-300 bg-white" data-testid="interview-offer-start" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-stone-500">Schedule notes</label>
                    <Input value={form.offer_schedule} onChange={set("offer_schedule")} placeholder="e.g. Mon/Wed/Fri 9a–1p" className="border-stone-300 bg-white" data-testid="interview-offer-schedule" />
                  </div>
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-700" data-testid="interview-provisional-label">
                  <input type="checkbox" checked={form.provisional} onChange={(e) => setForm((f) => ({ ...f, provisional: e.target.checked }))} data-testid="interview-provisional" />
                  Provisional hire
                </label>
                {form.provisional && (
                  <Textarea value={form.provisional_note} onChange={set("provisional_note")} placeholder="Provisional details (what's outstanding, deadline…)" className="min-h-16 border-stone-300 bg-white text-sm" data-testid="interview-provisional-note" />
                )}
                <div className="rounded-lg bg-primary/5 p-3 text-xs leading-relaxed text-stone-600">
                  Proceeding will: send the applicant an <b>offer letter signing link</b> (position + pay rate above — no login needed). The moment they sign: <b>reference-check links + employer verification requests go out automatically</b>, their employee account + employee ID is created, login invite sent, I-9 pre-filled from this interview, and any documents not captured today become outstanding onboarding items.
                </div>
              </div>
            )}
            {showed && (
              <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="interview-dedicated-caregiver">
                <h2 className="font-heading text-base font-semibold text-stone-900">Dedicated caregiver arrangement?</h2>
                <p className="mt-0.5 text-xs text-stone-500">They came in paired with one client they already know (family, friend, neighbor) and will work only with that client.</p>
                <div className="mt-3">
                  <YesNo value={form.dedicated_caregiver} onChange={(v) => setForm((f) => ({ ...f, dedicated_caregiver: v }))} testid="interview-dedicated" />
                </div>
                {form.dedicated_caregiver === "Yes" && (
                  <div className="mt-3 space-y-3 rounded-lg border border-primary/20 bg-primary/[0.03] p-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-stone-500">Which client? *</label>
                      <Input value={form.dedicated_client} onChange={(e) => setForm((f) => ({ ...f, dedicated_client: e.target.value }))} className="border-stone-300 bg-white" data-testid="interview-dedicated-client" />
                    </div>
                    <p className="text-xs leading-relaxed text-stone-600">
                      PA rules don't allow a paid caregiver to also be the person legally in charge of the client's decisions.
                      The client will sign a Designated Caregiver Addendum; the caregiver attests below.
                    </p>
                    <label className="flex cursor-pointer items-start gap-2 text-sm text-stone-700" data-testid="interview-dedicated-attest-label">
                      <input type="checkbox" checked={form.dedicated_attest} onChange={(e) => setForm((f) => ({ ...f, dedicated_attest: e.target.checked }))} className="mt-0.5" data-testid="interview-dedicated-attest" />
                      <span className="text-xs leading-relaxed">
                        The applicant certifies they are <b>not</b> the participant's spouse, Power of Attorney, legal
                        guardian, or representative payee; that they are <b>not eligible to provide paid CHC waiver services</b> to
                        this participant while holding any of these roles; and that they must notify Silver Home Care immediately
                        if this information changes, before providing another shift. A false answer is grounds for termination. *
                      </span>
                    </label>
                  </div>
                )}
              </section>
            )}
            {showed && (
              <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="interview-items-issued">
                <h2 className="font-heading text-base font-semibold text-stone-900">Items issued today</h2>
                <p className="mt-0.5 text-xs text-stone-500">Check what you gave them. Badge and clear bag are no charge — for the rest, enter how many and the price of each.</p>
                <div className="mt-3 space-y-2.5">
                  {[{ k: "badge", label: "Badge" }, { k: "clear_bag", label: "Clear bag" }].map(({ k, label }) => (
                    <label key={k} className="flex items-center gap-2 text-sm text-stone-700" data-testid={`item-${k}-label`}>
                      <input type="checkbox" checked={form.items[k]} onChange={(e) => setForm((f) => ({ ...f, items: { ...f.items, [k]: e.target.checked } }))} data-testid={`item-${k}`} />
                      {label} <span className="rounded-full bg-[#27AE60]/10 px-2 py-0.5 text-[10px] font-semibold text-[#1e8449]">no charge</span>
                    </label>
                  ))}
                  {[{ k: "shirt", label: "Shirt" }, { k: "pants", label: "Pants" }, { k: "hoodie", label: "Hoodie" }, { k: "sweatshirt", label: "Sweatshirt" }].map(({ k, label }) => {
                    const it = form.items[k];
                    const line = (parseFloat(it.price) || 0) * (parseInt(it.qty) || 0);
                    return (
                      <div key={k} className="flex flex-wrap items-center gap-2 text-sm text-stone-700" data-testid={`item-row-${k}`}>
                        <span className="w-24 font-medium">{label}</span>
                        <span className="text-xs text-stone-400">qty</span>
                        <Input type="number" min="0" value={it.qty} onChange={(e) => setForm((f) => ({ ...f, items: { ...f.items, [k]: { ...f.items[k], qty: e.target.value } } }))} className="h-8 w-16 border-stone-300 bg-white" data-testid={`item-${k}-qty`} />
                        <span className="text-xs text-stone-400">× $ each</span>
                        <Input value={it.price} onChange={(e) => setForm((f) => ({ ...f, items: { ...f.items, [k]: { ...f.items[k], price: e.target.value } } }))} placeholder="0.00" className="h-8 w-24 border-stone-300 bg-white" data-testid={`item-${k}-price`} />
                        {line > 0 && <span className="text-xs font-semibold text-stone-600">= ${line.toFixed(2)}</span>}
                      </div>
                    );
                  })}
                  {itemsTotal > 0 && <p className="pt-1 text-sm font-semibold text-stone-800" data-testid="items-total">Items total: ${itemsTotal.toFixed(2)}</p>}
                </div>
              </section>
            )}
            <Button onClick={submit} disabled={busy || !form.decision || (showed && !form.face_to_face) || (showed && form.dedicated_caregiver === "Yes" && (!form.dedicated_client.trim() || !form.dedicated_attest))} className="mt-4 rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="interview-save">
              {busy ? "Saving…" : "Save interview & decision"}
            </Button>
            {showed && !form.face_to_face && <p className="mt-2 text-[11px] text-stone-400">The face-to-face verification checkbox above is required.</p>}
          </div>
        </>
      )}
    </div>
  );
}
